import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ArrowRight, Globe, Zap, Shield } from 'lucide-react';
import styled from 'styled-components';
import { useLocale } from '../context/LocaleContext';
import { useT } from '../hooks/useT';

const Section = styled.section`
  position: relative;
  padding: 6rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: ${({ theme }) => theme.colors.muted};
  @media (min-width: 768px) {
    padding: 8rem 1.5rem;
  }
  @media (min-width: 1200px) {
    padding: 9rem 2rem;
  }
`;

const AccentBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: ${({ theme }) => theme.colors.gold};
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp.hero};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.foreground};
  max-width: 56rem;
  line-height: 1.2;
  margin: 0;
  @media (min-width: 1024px) {
    font-size: clamp(2.5rem, 3vw + 1.5rem, 4rem);
  }
`;

const TitleAccent = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const Subtitle = styled.p`
  margin: 1.25rem 0 0 0;
  font-size: ${({ theme }) => theme.fontClamp.lg};
  color: ${({ theme }) => theme.colors.mutedForeground};
  max-width: 40rem;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FormWrap = styled(motion.form)`
  margin-top: 2.5rem;
  width: 100%;
  max-width: 52rem;
  position: relative;
  z-index: 10;
`;

const InputWrap = styled.div`
  position: relative;
`;

const SearchIconAbs = styled.span`
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: flex;
`;

const HeroInput = styled.input`
  width: 100%;
  height: 4rem;
  padding-left: 3.5rem;
  padding-right: 9rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  font-size: ${({ theme }) => theme.fontClamp.base};
  outline: none;
  border: 2px solid transparent;
  transition: all 0.2s;
  box-shadow: ${({ theme }) => theme.shadowCard};
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
  @media (min-width: 1024px) {
    height: 4.5rem;
    font-size: ${({ theme }) => theme.fontClamp.lg};
  }
`;

const SubmitBtn = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  bottom: 0.5rem;
  padding: 0 2rem;
  border-radius: ${({ theme }) => theme.radius};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const ChipsWrap = styled(motion.div)`
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  z-index: 10;
`;

const Chip = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  box-shadow: ${({ theme }) => theme.shadowCard};
  transition: all 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const StepsGrid = styled(motion.div)`
  margin-top: 4rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;
  max-width: 48rem;
  z-index: 10;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StepRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  text-align: left;
`;

const StepIconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const StepLabel = styled.p`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  margin: 0;
`;

const StepDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.25rem 0 0 0;
`;

const steps = [
  { icon: Globe, labelKey: 'hero.step1Label', descKey: 'hero.step1Desc', bg: 'rgba(0,48,135,0.1)', iconColor: 'hsl(216, 100%, 27%)' },
  { icon: Zap, labelKey: 'hero.step2Label', descKey: 'hero.step2Desc', bg: 'rgba(220, 38, 38, 0.1)', iconColor: 'hsl(348, 91%, 47%)' },
  { icon: Shield, labelKey: 'hero.step3Label', descKey: 'hero.step3Desc', bg: 'rgba(128, 0, 0, 0.1)', iconColor: 'hsl(0, 100%, 25%)' },
];

function SearchHero() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { prependPath } = useLocale();
  const t = useT();

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(prependPath(`/search?q=${encodeURIComponent(query.trim())}`));
    }
  };

  return (
    <Section>
      <AccentBar />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.2, 0, 0, 1] }}
        style={{ position: 'relative', zIndex: 10 }}
      >
        <Badge>{t('hero.badge')}</Badge>
        <Title>
          {t('hero.titlePrefix')} <TitleAccent>{t('hero.titleAccent')}</TitleAccent>
        </Title>
        <Subtitle>
          {t('hero.subtitle')}
        </Subtitle>
      </motion.div>

      <FormWrap
        onSubmit={handleSearch}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0, 0, 1] }}
      >
        <InputWrap>
          <SearchIconAbs>
            <Search size={20} strokeWidth={2.5} />
          </SearchIconAbs>
          <HeroInput
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('hero.inputPlaceholder')}
          />
          <SubmitBtn type="submit">
            {t('common.search')} <ArrowRight size={16} strokeWidth={2.5} />
          </SubmitBtn>
        </InputWrap>
      </FormWrap>

      <ChipsWrap initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
        {['github.com', 'stripe.com', 'shopify.com', 'figma.com'].map((domain) => (
          <Chip key={domain} type="button" onClick={() => navigate(prependPath(`/site/${String(domain).replace(/\./g, '-').toLowerCase()}`))}>
            {domain}
          </Chip>
        ))}
      </ChipsWrap>

      <StepsGrid
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        {steps.map((step) => (
          <StepRow key={step.labelKey}>
            <StepIconBox $bg={step.bg} $color={step.iconColor}>
              <step.icon size={18} strokeWidth={2.5} />
            </StepIconBox>
            <div>
              <StepLabel>{t(step.labelKey)}</StepLabel>
              <StepDesc>{t(step.descKey)}</StepDesc>
            </div>
          </StepRow>
        ))}
      </StepsGrid>
    </Section>
  );
}

export default SearchHero;
